import { cva, type VariantProps } from 'class-variance-authority';
import {
  AlertCircle,
  Check,
  ChevronDown,
  ChevronRight,
  Clock,
  Code,
  Wrench,
} from 'lucide-react';
import * as React from 'react';
import { AICodeBlock } from '@/components/organisms/ai/AICodeBlock';
import { useControllableState } from '@/hooks/use-controllable-state';
import { cn } from '@/lib/utils';
import { WorkflowDisclosure } from '../_shared/workflow-step-renderer';

// =============================================================================
// Variants
// =============================================================================

const toolCallVariants = cva(
  'overflow-hidden rounded-xl border transition-colors duration-(--duration-base) ease-smooth motion-reduce:transition-none',
  {
    variants: {
      status: {
        pending: 'border-border bg-muted',
        running: 'border-primary bg-primary/10',
        success: 'border-success/50 bg-success/10',
        error: 'border-destructive/50 bg-destructive/10',
      },
    },
    defaultVariants: {
      status: 'pending',
    },
  },
);

const statusIconVariants = cva('size-4', {
  variants: {
    status: {
      pending: 'text-muted-foreground',
      running: 'text-primary animate-spin motion-reduce:animate-none',
      success: 'text-success',
      error: 'text-destructive',
    },
  },
  defaultVariants: {
    status: 'pending',
  },
});

// =============================================================================
// Types
// =============================================================================

export type ToolCallStatus = 'pending' | 'running' | 'success' | 'error';

export interface ToolCallPart {
  /** Unique tool call ID */
  toolCallId: string;
  /** Tool name */
  toolName: string;
  /** Tool input/arguments */
  args?: Record<string, unknown>;
  /** Tool output/result */
  result?: unknown;
  /** Tool status */
  status?: ToolCallStatus;
  /** Error message if status is error */
  error?: string;
}

// =============================================================================
// Status Icon Component
// =============================================================================

interface StatusIconProps {
  status: ToolCallStatus;
  className?: string;
}

const statusGlyphs = {
  pending: Wrench,
  running: Clock,
  success: Check,
  error: AlertCircle,
} as const satisfies Record<ToolCallStatus, React.ElementType>;

function StatusIcon({ status, className }: StatusIconProps) {
  return (
    <span
      data-slot="ai-tool-call-icon"
      aria-hidden="true"
      className="inline-grid shrink-0"
    >
      {(Object.keys(statusGlyphs) as ToolCallStatus[]).map((glyph) => {
        const Icon = statusGlyphs[glyph];
        const current = status === glyph;
        return (
          <span
            key={glyph}
            data-status={glyph}
            className={cn(
              // Persistent layers let CSS retarget a reversal from its current opacity.
              'col-start-1 row-start-1 grid place-items-center transition-opacity duration-(--duration-base) ease-smooth motion-reduce:transition-none',
              current ? 'opacity-100' : 'opacity-0',
            )}
          >
            <Icon
              className={cn(
                statusIconVariants({ status: glyph }),
                className,
                // Stop the outgoing clock immediately, not after the fade ends.
                !current && 'animate-none',
              )}
            />
          </span>
        );
      })}
    </span>
  );
}

StatusIcon.displayName = 'StatusIcon';

// =============================================================================
// Tool Call Header
// =============================================================================

interface AIToolCallHeaderProps {
  toolName: string;
  status: ToolCallStatus;
  isOpen: boolean;
  onToggle: () => void;
  className?: string;
}

// Keep the exported header's props unchanged; panel wiring belongs to AIToolCall.
function AIToolCallHeader(props: AIToolCallHeaderProps) {
  return <ToolCallHeaderButton {...props} />;
}

function ToolCallHeaderButton({
  toolName,
  status,
  isOpen,
  onToggle,
  className,
  triggerRef,
  contentId,
}: AIToolCallHeaderProps & {
  triggerRef?: React.RefObject<HTMLButtonElement | null>;
  contentId?: string;
}) {
  const statusLabel = {
    pending: 'Pending',
    running: 'Running...',
    success: 'Completed',
    error: 'Failed',
  };

  return (
    <button
      type="button"
      ref={triggerRef}
      onClick={onToggle}
      aria-expanded={isOpen}
      aria-controls={contentId}
      className={cn(
        'flex items-center justify-between gap-3 w-full px-4 py-3 transition-colors duration-(--duration-base) ease-smooth motion-reduce:transition-none hover:bg-foreground/5 focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-ring',
        className,
      )}
    >
      <div className="flex min-w-0 items-center gap-3">
        <StatusIcon status={status} />
        <div className="flex min-w-0 flex-col items-start text-start">
          <span className="max-w-full truncate text-sm font-medium text-foreground">
            {toolName}
          </span>
          <span className="text-xs text-secondary-foreground">
            {statusLabel[status]}
          </span>
        </div>
      </div>
      {isOpen ? (
        <ChevronDown
          className="size-4 shrink-0 text-muted-foreground"
          aria-hidden="true"
        />
      ) : (
        <ChevronRight
          className="size-4 shrink-0 text-muted-foreground rtl:rotate-180"
          aria-hidden="true"
        />
      )}
    </button>
  );
}

AIToolCallHeader.displayName = 'AIToolCallHeader';
ToolCallHeaderButton.displayName = 'ToolCallHeaderButton';

// =============================================================================
// Tool Call Section
// =============================================================================

interface ToolCallSectionProps {
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}

function ToolCallSection({
  title,
  icon,
  children,
  className,
}: ToolCallSectionProps) {
  return (
    <div className={cn('space-y-2', className)}>
      <div className="flex items-center gap-2 text-xs font-medium text-secondary-foreground uppercase tracking-wide">
        {icon}
        {title}
      </div>
      {children}
    </div>
  );
}

ToolCallSection.displayName = 'ToolCallSection';

// =============================================================================
// Main AIToolCall Component
// =============================================================================

export interface AIToolCallProps extends VariantProps<typeof toolCallVariants> {
  /** Tool call data */
  toolPart: ToolCallPart;
  /** Whether to expand by default */
  defaultOpen?: boolean;
  /** Controlled open state */
  open?: boolean;
  /** Callback when open state changes */
  onOpenChange?: (open: boolean) => void;
  /** Additional class name */
  className?: string;
}

function formatValue(value: unknown): string {
  if (typeof value === 'string') return value;
  return JSON.stringify(value, null, 2);
}

function AIToolCall({
  toolPart,
  defaultOpen = false,
  open: controlledOpen,
  onOpenChange,
  className,
}: AIToolCallProps) {
  const [isOpen, setIsOpen] = useControllableState({
    value: controlledOpen,
    defaultValue: defaultOpen,
    onChange: onOpenChange,
  });

  const status = toolPart.status || 'pending';
  const contentId = React.useId();
  const triggerRef = React.useRef<HTMLButtonElement>(null);

  const handleToggle = () => {
    setIsOpen(!isOpen);
  };

  return (
    <div
      data-slot="ai-tool-call"
      className={cn(toolCallVariants({ status }), className)}
    >
      <ToolCallHeaderButton
        toolName={toolPart.toolName}
        status={status}
        isOpen={isOpen}
        onToggle={handleToggle}
        triggerRef={triggerRef}
        contentId={contentId}
      />

      <div aria-hidden={!isOpen}>
        <WorkflowDisclosure
          open={isOpen}
          id={contentId}
          triggerRef={triggerRef}
          data-slot="ai-tool-call-panel"
        >
          <div data-slot="ai-tool-call-content" className="px-4 pb-4 space-y-4">
            {/* Tool Call ID */}
            <div className="text-xs text-muted-foreground font-mono">
              ID: {toolPart.toolCallId}
            </div>

            {/* Input Section */}
            {toolPart.args && Object.keys(toolPart.args).length > 0 && (
              <ToolCallSection title="Input" icon={<Code className="size-3" />}>
                <AICodeBlock
                  code={formatValue(toolPart.args)}
                  language="json"
                  showHeader={false}
                  showCopyButton={true}
                  size="sm"
                />
              </ToolCallSection>
            )}

            {/* Output Section */}
            {toolPart.result !== undefined && (
              <ToolCallSection
                title="Output"
                icon={<Code className="size-3" />}
              >
                <AICodeBlock
                  code={formatValue(toolPart.result)}
                  language="json"
                  showHeader={false}
                  showCopyButton={true}
                  size="sm"
                />
              </ToolCallSection>
            )}

            {/* Error Section */}
            {toolPart.error && (
              <div className="p-3 rounded-lg bg-destructive/20 border border-destructive/50">
                <div className="flex items-start gap-2">
                  <AlertCircle className="size-4 text-destructive mt-0.5 shrink-0" />
                  <p className="text-sm text-destructive">{toolPart.error}</p>
                </div>
              </div>
            )}
          </div>
        </WorkflowDisclosure>
      </div>
    </div>
  );
}

AIToolCall.displayName = 'AIToolCall';

// =============================================================================
// Tool Call List Component
// =============================================================================

export interface AIToolCallListProps {
  /** Array of tool calls */
  toolCalls: readonly ToolCallPart[];
  /** Whether to expand all by default */
  defaultAllOpen?: boolean;
  /** Tool-call IDs to expand initially in uncontrolled mode */
  defaultOpenToolCallIds?: readonly string[];
  /** Controlled expanded tool-call IDs */
  openToolCallIds?: readonly string[];
  /** Called with expanded IDs in current tool-call order */
  onOpenToolCallIdsChange?: (ids: readonly string[]) => void;
  /** Additional class name */
  className?: string;
}

function normalizeOpenToolCallIds(
  ids: readonly string[],
  toolCalls: readonly ToolCallPart[],
): string[] {
  const requested = new Set(ids);
  return toolCalls.flatMap((toolCall) =>
    requested.has(toolCall.toolCallId) ? [toolCall.toolCallId] : [],
  );
}

function areToolCallIdsEqual(
  left: readonly string[],
  right: readonly string[],
): boolean {
  return (
    left.length === right.length &&
    left.every((id, index) => id === right[index])
  );
}

function AIToolCallList({
  toolCalls,
  defaultAllOpen = false,
  defaultOpenToolCallIds = [],
  openToolCallIds: controlledOpenToolCallIds,
  onOpenToolCallIdsChange,
  className,
}: AIToolCallListProps) {
  const isControlled = controlledOpenToolCallIds !== undefined;
  const [uncontrolledOpenToolCallIds, setUncontrolledOpenToolCallIds] =
    React.useState<readonly string[]>(() =>
      normalizeOpenToolCallIds(
        defaultAllOpen
          ? toolCalls.map((toolCall) => toolCall.toolCallId)
          : defaultOpenToolCallIds,
        toolCalls,
      ),
    );
  const rawOpenToolCallIds = isControlled
    ? controlledOpenToolCallIds
    : uncontrolledOpenToolCallIds;
  const visibleOpenToolCallIds = normalizeOpenToolCallIds(
    rawOpenToolCallIds,
    toolCalls,
  );
  const openSet = new Set(visibleOpenToolCallIds);

  React.useLayoutEffect(() => {
    if (isControlled) return;
    setUncontrolledOpenToolCallIds((current) => {
      const normalized = normalizeOpenToolCallIds(current, toolCalls);
      return areToolCallIdsEqual(current, normalized) ? current : normalized;
    });
  }, [isControlled, toolCalls]);

  const setToolCallOpen = (toolCallId: string, open: boolean) => {
    const next = new Set(visibleOpenToolCallIds);
    if (open) next.add(toolCallId);
    else next.delete(toolCallId);
    const normalized = normalizeOpenToolCallIds([...next], toolCalls);
    if (!isControlled) {
      setUncontrolledOpenToolCallIds(normalized);
    }
    onOpenToolCallIdsChange?.([...normalized]);
  };

  return (
    <div data-slot="ai-tool-call-list" className={cn('space-y-2', className)}>
      {toolCalls.map((toolCall) => (
        <AIToolCall
          key={toolCall.toolCallId}
          toolPart={toolCall}
          open={openSet.has(toolCall.toolCallId)}
          onOpenChange={(open) => setToolCallOpen(toolCall.toolCallId, open)}
        />
      ))}
    </div>
  );
}

AIToolCallList.displayName = 'AIToolCallList';

export {
  AIToolCall,
  AIToolCallHeader,
  AIToolCallList,
  StatusIcon,
  statusIconVariants,
  ToolCallSection,
  toolCallVariants,
};
